import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { KeyRound, Plus, Copy, Loader2, Ban, RefreshCw, Trash2, CheckCircle2 } from "lucide-react";

export default function ApiKeys() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = (user?.permissions || []).includes("apikeys.manage");
  const [createOpen, setCreateOpen] = useState(false);
  const [revealKey, setRevealKey] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: async () => (await api.get("/api-keys")).data.data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["api-keys"] });

  const revoke = async () => {
    try { await api.post(`/api-keys/${revokeTarget.id}/revoke`); toast.success("API key revocata"); setRevokeTarget(null); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const regenerate = async (id) => {
    try { const { data } = await api.post(`/api-keys/${id}/regenerate`); setRevealKey(data.data); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/api-keys/${id}`); toast.success("Eliminata"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="API Key" subtitle="Crea e gestisci le chiavi per accedere alle API di Connectivity" icon={KeyRound}
        actions={canManage && <Button className="rounded-lg" onClick={() => setCreateOpen(true)} data-testid="create-key-btn"><Plus className="mr-1 h-4 w-4" /> Nuova API key</Button>} />

      <div className="space-y-3">
        {isLoading ? Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />) : (
          data?.keys.length === 0 ? (
            <Card className="border-dashed"><CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <KeyRound className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Nessuna API key. Creane una per iniziare a integrare.</p>
            </CardContent></Card>
          ) : data?.keys.map((k) => (
            <Card key={k.id} data-testid={`key-${k.id}`}>
              <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{k.name}</p>
                    <Badge variant={k.environment === "production" ? "default" : "secondary"} className={k.environment === "production" ? "bg-blue-600" : ""}>{k.environment === "production" ? "Produzione" : "Test"}</Badge>
                    <Badge variant="outline" className={k.status === "active" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-red-500/40 bg-red-500/10 text-red-600"}>{k.status === "active" ? "Attiva" : "Revocata"}</Badge>
                  </div>
                  {k.description && <p className="mt-1 text-xs text-muted-foreground">{k.description}</p>}
                  <p className="mt-2 font-mono text-sm text-muted-foreground">{k.masked}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {k.permissions.map((p) => <Badge key={p} variant="secondary" className="text-[10px] font-normal">{p}</Badge>)}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Ultimo utilizzo: {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString("it-IT") : "mai"}
                    {k.last_used_ip && ` · IP ${k.last_used_ip}`}
                  </p>
                </div>
                {canManage && k.status === "active" && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="rounded-lg" onClick={() => regenerate(k.id)} data-testid={`regen-${k.id}`}><RefreshCw className="mr-1 h-3.5 w-3.5" /> Rigenera</Button>
                    <Button variant="outline" size="sm" className="rounded-lg text-destructive" onClick={() => setRevokeTarget(k)} data-testid={`revoke-${k.id}`}><Ban className="mr-1 h-3.5 w-3.5" /> Revoca</Button>
                  </div>
                )}
                {canManage && k.status !== "active" && (
                  <Button variant="ghost" size="icon" onClick={() => remove(k.id)}><Trash2 className="h-4 w-4" /></Button>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <CreateKeyDialog open={createOpen} onOpenChange={setCreateOpen} permissions={data?.available_permissions || []} onCreated={(res) => { setRevealKey(res); refresh(); }} />

      {/* Reveal once dialog */}
      <Dialog open={!!revealKey} onOpenChange={(o) => !o && setRevealKey(null)}>
        <DialogContent data-testid="reveal-key-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-primary" /> API key generata</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700">
              Copia questa chiave adesso. Per motivi di sicurezza non sarà più visibile.
            </div>
            <div className="flex gap-2">
              <Input readOnly value={revealKey?.full_key || ""} className="font-mono text-xs" data-testid="full-key-value" />
              <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(revealKey.full_key); toast.success("Copiata negli appunti"); }} data-testid="copy-key-btn"><Copy className="h-4 w-4" /></Button>
            </div>
          </div>
          <DialogFooter><Button className="rounded-lg" onClick={() => setRevealKey(null)}>Ho copiato la chiave</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revocare la API key?</AlertDialogTitle>
            <AlertDialogDescription>La chiave "{revokeTarget?.name}" smetterà immediatamente di funzionare. Le applicazioni che la usano perderanno l'accesso.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={revoke} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-revoke">Revoca</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateKeyDialog({ open, onOpenChange, permissions, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", environment: "test", permissions: [], expires_at: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));

  const submit = async () => {
    if (!form.name) return toast.error("Inserisci un nome");
    setLoading(true);
    try {
      const payload = { ...form, expires_at: form.expires_at || null };
      const { data } = await api.post("/api-keys", payload);
      toast.success("API key creata");
      onCreated(data.data);
      onOpenChange(false);
      setForm({ name: "", description: "", environment: "test", permissions: [], expires_at: "" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="create-key-dialog">
        <DialogHeader><DialogTitle>Nuova API key</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Nome</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="key-name" /></div>
          <div className="space-y-2"><Label>Descrizione</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Ambiente</Label>
              <Select value={form.environment} onValueChange={(v) => set("environment", v)}>
                <SelectTrigger data-testid="key-env"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="test">Test</SelectItem><SelectItem value="production">Produzione</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Scadenza (opzionale)</Label><Input type="date" value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} /></div>
          </div>
          <div className="space-y-2">
            <Label>Permessi</Label>
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-border p-3">
              {permissions.map((p) => (
                <label key={p} className="flex items-center gap-2 text-xs">
                  <Checkbox checked={form.permissions.includes(p)} onCheckedChange={() => togglePerm(p)} /> <span className="font-mono">{p}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={loading} className="rounded-lg" data-testid="submit-key-btn">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Crea key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
